import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AccountGroup } from '@galaxy/account-group';
import { SNAPSHOT_REPORT_SKU } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subscription, combineLatest, skipUntil } from 'rxjs';
import { filter, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AppConfigService } from '../../../../../../app-config.service';
import { CheckoutDialogComponent } from '../../../../../../checkout/checkout-dialog.component';
import { UnifiedTOSDialogComponent } from '../../../../../../common/tos-unified/unified-tos-dialog.component';
import { AccountService } from '../../../../../../core/account.service';
import { CurrentSnapshot, SnapshotService } from '../../../../../../core/snapshot.service';
import { UnifiedTOSService } from '../../../../../../core/tos-unified.service';
import { DynamicOpenCloseTemplateRefService } from '../../../../../../side-drawer/dynamic-open-close-template-ref.service';

@Component({
  selector: 'app-create-snapshot',
  templateUrl: './create-snapshot.component.html',
  styleUrls: ['./create-snapshot.component.scss'],
})
export class CreateSnapshotComponent implements OnInit, AfterViewInit, OnDestroy {
  static readonly scorecardTemplateID = 'openScorecard';
  @Input()
  accountGroup: AccountGroup;
  @Input()
  showText = true;
  @Input()
  originDetails: string;
  @Input()
  set snapshotIsReady(isReady: boolean) {
    this.snapshotIsReady$$.next(isReady);
  }
  snapshotIsReady$$ = new BehaviorSubject<boolean>(true);
  @Output()
  snapshotCreated = new EventEmitter<boolean>(false);
  @ViewChildren('openScorecard') openScorecardTemplate: QueryList<TemplateRef<any>>;

  subscriptions: Subscription[] = [];
  tosDialog: MatDialogRef<UnifiedTOSDialogComponent>;
  partnerId: string;

  private displaySnapshotSummaryClick$$: BehaviorSubject<Event> = new BehaviorSubject<Event>(null);
  displaySnapshotSummaryClick$: Observable<Event> = this.displaySnapshotSummaryClick$$
    .asObservable()
    .pipe(filter((a) => !!a));

  private agsLoadingCreateSnapshot: string[] = [];
  private isImpersonated: boolean;
  private showTOSDialog: boolean;

  currentSnapshot$: Observable<CurrentSnapshot>;
  snapshotLoading$: Observable<boolean>;

  hasEditAccess$: Observable<boolean>;

  constructor(
    private readonly snackbarService: SnackbarService,
    public dialog: MatDialog,
    private readonly snapshotService: SnapshotService,
    private readonly tosService: UnifiedTOSService,
    private readonly appConfig: AppConfigService,
    private dynamicOpenCloseService: DynamicOpenCloseTemplateRefService,
    private activatedRoute: ActivatedRoute,
    private readonly accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.isImpersonated = this.appConfig.config.isImpersonated;
    this.subscriptions.push(
      this.tosService.tosDataLoaded$.subscribe((isReady) => {
        if (isReady) {
          if (!this.tosService.hasAcceptedTOS && !this.tosService.isClassicSubscription) {
            this.showTOSDialog = true;
          }
        }
      }),
    );

    this.partnerId = this.appConfig.config.partnerId;

    this.currentSnapshot$ = this.accountService.accountID$.pipe(
      switchMap((id) => this.snapshotService.getCurrentSnapshotUpdates$(id)),
    );

    this.snapshotLoading$ = this.accountService.accountID$.pipe(
      switchMap((id) => this.snapshotService.currentSnapshotLoading$(id)),
    );

    const snapshotBeingCreated$ = this.accountService.accountID$.pipe(
      switchMap((id) => this.snapshotService.createSnapshotLoading$(id)),
    );

    const createSnapshotSuccess$ = this.accountService.accountID$.pipe(
      switchMap((id) => this.snapshotService.createSnapshotSuccess$(id)),
      skipUntil(snapshotBeingCreated$.pipe(filter((is) => is === true))),
    );

    this.subscriptions.push(
      createSnapshotSuccess$
        .pipe(
          withLatestFrom(this.appConfig.config$),
          tap(([success, config]) => {
            if (success) {
              const chmln = window['chmln'];
              chmln.identify(config.unifiedUserId);
              chmln.track('Created Snapshot');
            }
          }),
        )
        .subscribe(([success, _]) =>
          success
            ? this.snackbarService.openSuccessSnack('Successfully created a snapshot')
            : this.snackbarService.openErrorSnack('Failed to create snapshot. Please try again.'),
        ),
    );

    this.hasEditAccess$ = this.snapshotService.partnerHasAccessToSnapshotEditing();
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.openScorecardTemplate.changes.pipe(startWith(this.openScorecardTemplate), take(1)).subscribe((ref) => {
        this.dynamicOpenCloseService.registerTemplate(CreateSnapshotComponent.scorecardTemplateID, ref.first);
      }),
    );

    this.subscriptions.push(
      this.displaySnapshotSummaryClick$.subscribe(() => {
        this.dynamicOpenCloseService.open(CreateSnapshotComponent.scorecardTemplateID);
      }),
    );

    // Automatically open snapshot panel for account given url param
    combineLatest([this.currentSnapshot$, this.activatedRoute.queryParams])
      .pipe(take(1))
      .subscribe(([currentSnapshot, params]) => {
        if (!!currentSnapshot && params?.openPanel === 'snapshot-scorecard') {
          this.dynamicOpenCloseService.open(CreateSnapshotComponent.scorecardTemplateID);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.dynamicOpenCloseService.close(CreateSnapshotComponent.scorecardTemplateID);
  }

  createSnapshot(inferMissingData: boolean): void {
    this.snapshotService.doCreateSnapshotWork(this.accountGroup.accountGroupId, [this.originDetails], inferMissingData);
  }

  private isAccountGroupLoadingCreateSnapshot(accountGroupId: string): boolean {
    return this.agsLoadingCreateSnapshot.indexOf(accountGroupId) >= 0;
  }

  onSnapshotCreatePressed(accountGroup: AccountGroup): void {
    this.dynamicOpenCloseService.close(CreateSnapshotComponent.scorecardTemplateID);
    if (this.showTOSDialog && !this.isImpersonated) {
      this.openTOSDialog(this.onCreateSnapshot.bind(this), accountGroup, '');
      return;
    }
    this.onCreateSnapshot(accountGroup);
  }

  private onCreateSnapshot(accountGroup: AccountGroup): void {
    if (this.isAccountGroupLoadingCreateSnapshot(this.accountGroup.accountGroupId)) {
      return;
    }
    this.setLoadingCreateSnapshot(this.accountGroup.accountGroupId, true);
    this.openConfirmSnapshotCreateDialog(accountGroup)
      .pipe(take(1))
      .subscribe((r) => {
        if (r?.status !== 'success') {
          this.setLoadingCreateSnapshot(this.accountGroup.accountGroupId, false);
          return;
        }
        this.createSnapshot(r.inferMissingData);
      });
  }

  private openConfirmSnapshotCreateDialog(_accountGroup: AccountGroup): Observable<any> {
    this.dialog.closeAll();
    const dialog: MatDialogRef<CheckoutDialogComponent> = this.dialog.open(CheckoutDialogComponent, {
      width: '480px',
      data: {
        partnerId: this.partnerId,
        title: 'Create Snapshot Report',
        successLabel: 'Create',
        productSKU: SNAPSHOT_REPORT_SKU,
        createButtonId: 'snapshotCreateButton',
        successId: 'snapshotCreateButton',
        accountGroupId: this.accountGroup.accountGroupId,
      },
    });
    return dialog.afterClosed();
  }

  private openTOSDialog(
    callback: (accountGroup: AccountGroup, verb: string) => void,
    accountGroup?: AccountGroup,
    verb?: string,
  ): void {
    if (this.tosDialog) {
      return;
    }
    this.tosDialog = this.dialog.open(UnifiedTOSDialogComponent, UnifiedTOSDialogComponent.config());
    this.subscriptions.push(
      this.tosDialog.afterClosed().subscribe(() => {
        this.tosDialog = null;
        if (this.tosService.hasAcceptedTOS) {
          this.showTOSDialog = false;
          callback(accountGroup, verb);
        }
      }),
    );
  }

  private setLoadingCreateSnapshot(accountGroupId: string, loading: boolean): void {
    if (loading) {
      this.agsLoadingCreateSnapshot.push(this.accountGroup.accountGroupId);
    } else {
      this.agsLoadingCreateSnapshot.splice(this.agsLoadingCreateSnapshot.indexOf(accountGroupId), 1);
    }
  }

  snapshotSummaryClick(event: Event): void {
    this.displaySnapshotSummaryClick$$.next(event);
  }
}
